<template>
    <div class="container container--utilisateur">  

        <Loader v-if="!ready || processing"></Loader>

        <article class="utilisateur" v-if="ready">

            <header class="utilisateur__heading">
                <h1 class="woocommerce-order-details__title" v-if="isNew">Créer un nouvel utilisateur</h1>
                <h1 class="woocommerce-order-details__title" v-if="!isNew">Modifier l'utilisateur</h1>
            </header> 

            <Edit class="edit"
                @submit.native="checkForm">

                <Message v-if="Object.values(this.errors).length" type="important">
                    <ul>
                        <li v-for="(message, index) in Object.values(this.errors)" :key="index">{{ message }}</li>
                    </ul>
                </Message>

                <fieldset class="edit__fieldset">
                    <div class="edit__field edit__field--main"
                        :class="{ 'error': errors.name }"> 
                        <label class="edit__field__label" for="name">Nom</label>
                        <input class="edit__field__input"
                            type="text"
                            id="name"
                            v-model="utilisateur.name"
                            placeholder="Nom de l'utilisateur" />
                    </div>

                    <div class="edit__field"
                        :class="{ 'error': errors.profil }"> 
                        <label class="edit__field__label" for="profil">Profil</label>
                        <select class="edit__field__input"
                            id="profil"
                            v-model="utilisateur.profilId"
                            @change="selectProfil()">
                            <option :value="null" disabled="disabled">Sélectionner un profil</option>
                            <option v-for="profil in profils"
                                :key="profil.id"
                                :value="profil.id">
                                {{ profil.nom }}
                            </option>
                        </select>
                    </div>

                    <div class="edit__field"
                        :class="{ 'error': errors.email }"> 
                        <label class="edit__field__label" for="email">Adresse e-mail</label>
                        <input class="edit__field__input"
                            type="email"
                            id="email"
                            v-model="utilisateur.email"
                            placeholder="Adresse e-mail" />
                    </div>

                    <div class="edit__field"
                        :class="{ 'error': errors.password }"> 
                        <label class="edit__field__label" for="password">Mot de passe</label>
                        <input class="edit__field__input"
                            type="password"
                            id="password"
                            v-model="utilisateur.password"
                            placeholder="Nouveau mot de passe" />
                    </div>

                    <div class="edit__field"
                        :class="{ 'error': errors.validityFrom || errors.validityTo }"> 
                        <label class="edit__field__label" for="validityFrom">Valide du</label>
                        <Datepicker class="edit__field__input"
                            id="validityFrom"
                            :language="datepickerLocale"
                            :monday-first="datepickerMondayFirst"
                            v-model="utilisateur.validiteDebut"
                            placeholder="jj/mm/aaaa" />
                        <label class="edit__field__label" for="validityTo"> au </label>
                        <Datepicker class="edit__field__input"
                            id="validityTo"
                            :language="datepickerLocale"
                            :monday-first="datepickerMondayFirst"
                            v-model="utilisateur.validiteFin"
                            placeholder="jj/mm/aaaa" />
                    </div>

                </fieldset>

                <fieldset class="edit__fieldset">
                    <h3>Habilitations</h3>

                    <ul class="habilitations">
                        <li class="habilitation__item"
                            v-for="habilitation in habilitations"
                            :key="habilitation.id">

                            <div class="habilitation">
                                <div class="habilitation__name">
                                    <label :for="'cb_habilitation_' + habilitation.id">                                        
                                        <input class="woocommerce-form__input woocommerce-form__input-checkbox"
                                            type="checkbox"                                            
                                            :disabled="!isHabilitationEditable"
                                            :id="'cb_habilitation_' + habilitation.id"
                                            :value="habilitation.id"
                                            v-model="selectedHabilitations"
                                            @input="( $event.target.checked ) ? addHabilitationsDependencies(habilitation) : removeHabilitationsDependencies(habilitation)" />
                                        <span :class="{default:defaultHabilitations.indexOf(habilitation.id)>-1}">{{ habilitation.nom }}</span>
                                    </label>
                                </div>
                            </div>

                        </li>
                    </ul>
                </fieldset>              

                <fieldset class="edit__fieldset">
                    <div class="edit__field" :class="{ 'error': errors.emails }"> 
                        <h3>Emails à mettre en copie
                            <span>({{this.selectedEmailsCC.length}}/3)</span>
                        </h3>
                        <input-tag css="woocommerce-Input woocommerce-Input--text input-text"
                            v-model="selectedEmailsCC" 
                            :check="(value) => checkEmailCC(value)"
                            :max="3" />
                    </div>
                </fieldset>                

                <fieldset class="edit__fieldset">
                    <h3>Adresses                                                     
                        <span>({{this.selectedClientAddresses.length}}/{{countAdresses}})</span>
                    </h3>
                    
                    <ul class="clients"
                        v-for="client in clients"
                        :key="'client' + client.id">
                        <li class="clients__item">

                            <div class="client">
                            
                                <collapse :hidden="false" :selected="true">

                                    <div slot="collapse-header">
                
                                            <div class="client__name">
                                                <label @click.stop :for="'cb_client_' + client.id">  
                                                    <input class="woocommerce-form__input woocommerce-form__input-checkbox"
                                                        type="checkbox"
                                                        :checked="isAllAddressChecked(client)"
                                                        :id="'cb_client_' + client.id" 
                                                        :disabled="!isAddressEditable"
                                                        @input="( $event.target.checked ) ? addAllClientAddress(client) : removeAllClientAddress(client)"/>
                                                    <strong>Client {{ client.code }} — {{ client.name }} ({{getAddressesSelectedMessage(client)}})
                                                    </strong>
                                                </label>
                                            </div>
                                    </div>

                                    <div slot="collapse-body"> 

                                        <!-- Adresses client -->
                                        <ul class="client__addresses">
                                            <li class="client__addresses__item"
                                                v-for="clientAddress in client.clientAddresses"
                                                :id="'clientAddress_' + clientAddress.id"
                                                :key="'clientAddresss' + clientAddress.id">

                                                <div class="client-address"> 
                                                    <div class="client-address__content">       
                                                        <label class="woocommerce-form__label woocommerce-form__label-for-checkbox"
                                                            :for="'cb_clientAddress_' + clientAddress.id">
                                                            <input class="woocommerce-form__input woocommerce-form__input-checkbox"
                                                                type="checkbox"
                                                                v-model="selectedClientAddresses"
                                                                :id="'cb_clientAddress_' + clientAddress.id"
                                                                :disabled="!isAddressEditable"
                                                                :value="clientAddress.id"
                                                                @input="( !$event.target.checked ) ? unsetDefaultAddress(client, clientAddress) : ''"
                                                                />   
                                                            <span class="client-address__content__name">                           
                                                                <strong>{{ clientAddress.address.raisonSociale1 }} {{ clientAddress.address.raisonSociale2 }} <span v-if="clientAddress.address.city">({{ clientAddress.address.city }})</span></strong><br />
                                                                <span>{{ clientAddress.address.address1 }} {{ clientAddress.address.address2 }}{{ clientAddress.address.address3 }} {{ clientAddress.address.postalCode }} {{ clientAddress.address.city }}</span><br />
                                                                <small>{{ clientAddress.address.homePhone }}</small>                                
                                                            </span>
                                                        </label>
                                                    </div>

                                                    <div class="client-address__default">
                                                        <Badge type="info" v-if="isDefaultAddress(clientAddress)">Défaut</Badge>
                                                        <a v-if=" 
                                                            ((isNew && $setting('USER_CREATE_ENABLED')) || (!isNew && $setting('USER_UPDATE_ENABLED')))
                                                            && !isDefaultAddress(clientAddress) && isAddressChecked(clientAddress)"
                                                            href="#"
                                                            title="Choisir cette adresse comme celle par défaut"
                                                            v-on:click.prevent="setDefaultAddress(client, clientAddress)">Choisir par défaut</a>
                                                    </div>

                                                </div>

                                            </li>
                                        </ul>

                                    </div>

                                </collapse>   

                            </div>

                        </li>                   

                    </ul>

                </fieldset>                
                
                <editbar>                               
                    <button class="woocommerce-button button alt"
                        type="submit"
                        value="submit"
                        v-if="isNew && $setting('USER_CREATE_ENABLED')">Créer</button>                    
                    <button class="woocommerce-button button alt"
                        type="submit"
                        value="submit"
                        v-if="!isNew && $setting('USER_UPDATE_ENABLED')">Modifier</button>        
                    <router-link class="woocommerce-button button"
                        :to="{ name: 'utilisateurList' }">Annuler</router-link>
                </editbar>

            </Edit>

        </article>

    </div>
</template>

<script>
import { mapState } from 'vuex'

import api from '@/api/proxyWeb/utilisateurs'
import apiMe from '@/api/proxyWeb/me'
import { actions } from '@/store/utilisateurs'
import { actions as habilitationActions } from '@/store/habilitations'

import title from "@/containers/title";

import Datepicker from 'vuejs-datepicker'
import * as DATEPICKER_LOCALES from "vuejs-datepicker/src/locale"
import Badge from '@/components/badge/Badge.vue'
import Collapse from '@/components/collapse/Collapse.vue'
import Edit from '@/components/edit/Edit.vue'
import Editbar from '@/components/edit/Editbar.vue'
import Loader from '@/components/loader/Loader.vue'
import Message from '@/components/message/Message.vue';
import InputTag from '@/components/inputTag/InputTag.vue'
import { validateEmail } from '@/helpers/validation';

export default {

    name: 'Utilisateur',

    data: function() {
        return {
            datepickerLocale: DATEPICKER_LOCALES['fr'],
            datepickerMondayFirst: true,
            errors: {},
            profils: [],
            defaultHabilitations: [],
            utilisateur: {
                id: null,
                code: '',
                email: '',   
                name: '',
                profil: null,
                profilId: null,
                password: '',            
                validiteDebut: null,
                validiteFin: null,
                defaultAddressId: null,
                defaultClientId: null
            },
            selectedClientAddresses: [],
            selectedHabilitations: [],
            selectedEmailsCC: [],
            processing: false,
            ready: false,
            validateEmail: validateEmail
        }
    },

    computed: {

        isNew: function() { return ( this.ready && this.utilisateur.id === null ) },

        ...mapState({

            countAdresses: function() {
                let nbAddresses = 0
                this.clients.forEach ( client => {
                    nbAddresses += client.clientAddresses.length
                 })
                return nbAddresses
            },

            habilitations: state => state.habilitations.all,

            isAddressEditable: function() {

                /// Pour une modification, si l'association utilisateur / adresse n'est pas activée dans les réglages du plugin,
                /// on ne peut pas les modifier
                if ( !this.isNew && !this.$setting('USER_ADDRESS_UPDATE_ENABLED') ) {
                    return false
                }

                /// Pour une création, si l'association utilisateur / adresse n'est pas activée dans les réglages du plugin,
                /// on ne peut pas en ajouter
                if ( this.isNew && !this.$setting('USER_ADDRESS_CREATE_ENABLED') ) {
                    return false
                }

                return true   
            },                  

            isHabilitationEditable: function() {

                /// Pour une modification, si l'association utilisateur / habilitation n'est pas activée dans les réglages du plugin,
                /// on ne peut pas les modifier
                if ( !this.isNew && !this.$setting('USER_ABILITY_UPDATE_ENABLED') ) {
                    return false
                }

                /// Pour une création, si l'association utilisateur / habilitation n'est pas activée dans les réglages du plugin,
                /// on ne peut pas en ajouter
                if ( this.isNew && !this.$setting('USER_ABILITY_CREATE_ENABLED') ) {
                    return false
                }
                
                return true

            },

            clients: state => state.clients.all,

        })
    },

    methods: {
        
        addAllClientAddress: function(client) {

            client.clientAddresses.forEach( clientAddress => {
                if ( !this.selectedClientAddresses.includes(clientAddress.id) ) {
                    this.selectedClientAddresses.push(clientAddress.id)
                }
            })
        },

        checkEmailCC: function(emailcc) {
            return validateEmail(emailcc)
                && !this.selectedEmailsCC.includes(emailcc)
        },

        checkForm: function(e) {
            
            e.preventDefault()
            this.errors = {}
            if ( this.utilisateur.name === '' ) {
                this.errors.name = "Le nom de l'utilisateur est obligatoire."
            }
            if ( this.utilisateur.email === '' ) {
                this.errors.email = "Le Adresse e-mail est obligatoire."
            }
            if ( this.utilisateur.profilId == null ) {
                this.errors.profil = "Le profil est obligatoire."
            }                 

            if ( ( this.utilisateur.id !== null && this.utilisateur.password !== '' && this.utilisateur.password.length < 6 )
                || ( this.utilisateur.id === null && this.utilisateur.password.length < 6 ) ) {
                this.errors.password = "Utilisez 6 caractères ou plus pour votre mot de passe."
            }

            if ( !this.utilisateur.validiteDebut ) {
                this.errors.validityFrom = "La date de début de validité est obligatoire."
            }            
            if ( !this.utilisateur.validiteFin ) {
                this.errors.validityTo = "La date de fin de validité est obligatoire."
            }

            if ( !this.utilisateur.validiteDebut ) {
                this.errors.validityFrom = "La date de début de validité est obligatoire."
            }             

            if ( !this.utilisateur.defaultAddressId ) {
                this.errors.adresse = "Vous devez sélectionner une adresse par défaut."
            }

            if ( Object.values(this.errors).length > 0 ) {
                document.getElementById("top").scrollTo({top:0, behavior: 'smooth'});
                return
            }            


            this.processing = true
            this.utilisateur.emailsCC = this.selectedEmailsCC
            this.utilisateur.habilitationsIds = this.selectedHabilitations
            this.utilisateur.clientAddressIds = this.selectedClientAddresses

            let now = new Date()
            this.utilisateur.isActif = ( now >= new Date(this.utilisateur.validiteDebut) && now < new Date(this.utilisateur.validiteFin) )

            let action = ( this.isNew ) ? actions.ADD : actions.UPDATE            
            this.$store.dispatch(action, this.utilisateur)
                .then(() => {
                    const successMsg = ( this.isNew ) ? 'Utilisateur créé' : 'Utilisateur modifié'
                    this.$notify.success(successMsg)
                    this.$router.push({ name: "utilisateurList" })
                })
                .catch((e) => {
                    if ( e.response.status === 422 ) {
                        e.response.data.error.forEach((message, index) => {
                            this.errors[`remote_${index}`] = message
                        })     
                    }
                    else {
                        this.$error(  'Une erreur est survenue' )
                    }
                })
                .finally(() => {
                    this.processing = false
                    if ( Object.values(this.errors).length > 0 ) {           
                        document.getElementById("top").scrollTo({top:0, behavior: 'smooth'});
                    }  
                })
        },

        getAddressesSelectedMessage: function(client) {
            let nbSelectedAddresses = 0
            client.clientAddresses.forEach( clientAddress => {
                    if ( this.isAddressChecked(clientAddress) ) {
                        nbSelectedAddresses++
                    }
                })

            let info = nbSelectedAddresses + " adresse" + (nbSelectedAddresses> 1 ? "s": "") + 
                       " sélectionnée" + (nbSelectedAddresses> 1 ? "s": "") + " sur " +
                       client.clientAddresses.length + " possible" + (client.clientAddresses.length> 1 ? "s ": " ")
            return info
        },

        isAddressChecked: function(clientAddress) {
            
            let checked = false
            this.selectedClientAddresses.forEach(clientAddressId => {
                if ( clientAddressId === clientAddress.id ) {
                    checked = true
                }
            });

            return checked
        },  

        isAllAddressChecked: function(client) {

            let all = true

            if (client.clientAddresses.length == 0) {
                all = false
            }
            else {
                client.clientAddresses.forEach( clientAddress => {
                    if ( !this.isAddressChecked(clientAddress) ) {
                        all = false
                    }
                })
            }
            
            return all
        },      
            
        isDefaultAddress: function(clientAddress) {         
            return ( this.utilisateur.defaultAddressId === clientAddress.id )
        },

        addHabilitationsDependencies: function(habilitation)
        {
            habilitation.includes.forEach ( include => {
                if ( this.selectedHabilitations.indexOf(include)==-1){
                    this.selectedHabilitations.push(include)
                }
            })
        },

        removeHabilitationsDependencies: function(habilitation)
        {
            habilitation.includedIn.forEach ( include => {
                if (this.selectedHabilitations.indexOf(include)>-1) {
                    this.selectedHabilitations.splice(this.selectedHabilitations.indexOf(include), 1)
                }
            })
        },

        removeAllClientAddress: function(client) {

            client.clientAddresses.forEach( clientAddress => {
                const index = this.selectedClientAddresses.indexOf(clientAddress.id);
                if ( index > -1 ) {
                    this.selectedClientAddresses.splice(index, 1);
                }
            })

            if ( this.utilisateur.defaultClientId == client.id ){
                this.utilisateur.defaultAddressId = null
                this.utilisateur.defaultClientId = null
            }            
        },        

        selectProfil: function() {
            this.selectedHabilitations = this.profils[this.utilisateur.profilId - 1].habilitationsIds;
            this.defaultHabilitations = this.profils[this.utilisateur.profilId - 1].habilitationsIds;
        },

        setDefaultAddress: function(client, clientAddress) {
            this.utilisateur.defaultClientId = client.id
            this.utilisateur.defaultAddressId = clientAddress.id
        },

        unsetDefaultAddress: function(client, clientAddress) {

            if ( this.utilisateur.defaultAddressId == clientAddress.id ){
                this.utilisateur.defaultAddressId = null
                this.utilisateur.defaultClientId = null
            }
        },        
    },

  
    created() {

        this.id = ( this.$route.params.id ) ?? null

        let promises = [
            this.$store.dispatch(habilitationActions.GET_ALL),
            apiMe.getProfils()
        ]

        if ( this.id ) {
            promises.push(api.get(this.id))
        }

        Promise.all(promises)
            .then( data => {

                this.profils = data[1]
                if ( data.length > 2 ) {
                    this.utilisateur = data[2]
                    this.utilisateur.profilId = this.utilisateur.profil.id
                    this.utilisateur.password = ''
                    this.selectedClientAddresses = [ ...this.utilisateur.clientAddressIds ]
                    this.selectedHabilitations = [ ...this.utilisateur.habilitationsIds ]
                    this.selectedEmailsCC = [ ...this.utilisateur.emailsCC ]
                }

                this.ready = true
            })
            .catch(error => {
                this.$errorPage(error.reason)
            })

    },

    components: {
        Badge,
        Collapse,
        Datepicker,
        Edit,
        Editbar,
        InputTag,
        Loader,
        Message
    },

    mixins: [ title ]
}
</script>

<style>

.clients,
.client__addresses {
    list-style: none;
    margin: 0;
    padding: 0;
}

.clients input[type="checkbox"] {
    margin-right: 0.5rem;
}

.client__addresses__item {
    background-color: #f4f4f4;
    margin-bottom: 2px;
    padding: 0.5rem 2rem 0.5rem 6%;
}

.client__name {    
    padding: 1rem;
}

.client-address {
    display: flex;
    flex-direction: row;
}

.client-address__content {
    flex: 1;
}

.client-address__content label {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
}

.client-address__content label input[type="checkbox"] {
    top: 0.5em;
}

.client-address__content__name {
    flex: 1;
}

.client-address__content__name strong {
    line-height: 2em;
}

.client-address__default {
    display: flex;
    flex-direction: row;
    align-items: center;
}

.habilitations,
.habilitations__item {
    list-style-type: none;
    margin: 0;
}

.habilitations .default {
    font-weight: bold;
    font-style: italic;
}

.habilitations .habilitation__name {
    padding: 0.5rem 1rem;
}

.utilisateurs,
.utilisateurs__item {
    list-style-type: none;
    margin: 0;
}

.utilisateur .utilisateur__name {
    padding: 0.5rem 1rem;
}

.negative {
    color: rgb(255, 48, 0);
}  



</style>
